import { ROUTES } from '@/routes/paths';
import type { NotificationItem } from '../types';

/**
 * Resolves the route a notification should navigate to when clicked.
 * Uses `related_entity_type` first, then falls back to inferring from the
 * notification `type` string (e.g. "task_assigned" → tasks).
 *
 * Returns null if no meaningful destination can be determined.
 */
export function notificationRoute(n: NotificationItem): string | null {
  // Checked ahead of everything below: related_entity_type 'invoice' already
  // maps to ROUTES.accountsSales (a generic list), which is the wrong
  // destination for "someone asked to edit a finalized invoice" — that
  // needs the approval queue itself, or an Admin/Superadmin clicking the
  // notification lands somewhere they still have to go find it from.
  if (n.type === 'invoice_edit_requested') return ROUTES.invoiceEditApprovals;
  // Local transport amount pending / ready (2026-10-05) -> the order on the Dispatch screen.
  if ((n.type.startsWith('local_transport_') || n.type === 'transport_freight_pending') && n.related_entity_id) return ROUTES.dispatchOrderDetail.replace(':id', encodeURIComponent(n.related_entity_id));

  const entity = n.related_entity_type?.toLowerCase();
  const id = n.related_entity_id ? encodeURIComponent(n.related_entity_id) : '';
  const timeline = n.type.startsWith('appointment') ? 'appointments'
    : n.type.startsWith('followup') || n.type === 'follow_up_set' ? 'followups' : '';
  if (id && entity) {
    const section = timeline ? `#${timeline}` : '';
    if (['lead', 'leads', 'deal', 'deals', 'enquiry'].includes(entity)) {
      const parent = entity === 'leads' ? 'lead' : entity === 'deals' ? 'deal' : entity;
      return `${ROUTES.pipelineDetail.replace(':id', id)}${timeline ? `?timelineParent=${parent}` : ''}${section}`;
    }
    // A notification ABOUT a contact opens that contact, not the whole directory
    // (e.g. "your client just called a colleague" — the whole point is to ring them).
    if (entity === 'contact' || entity === 'contacts') return ROUTES.contactDetail.replace(':id', id);
    if (entity === 'quotation') return `${ROUTES.quotationDetail.replace(':id', id)}${section}`;
    if (entity === 'proforma') return `${ROUTES.proformaDetail.replace(':id', id)}${section}`;
    if (timeline && (entity === 'invoice' || entity === 'sales')) return `${ROUTES.invoiceDetail.replace(':id', id)}${section}`;
    // Every notification about one record opens THAT record (client, 2026-10-06),
    // not the list it lives in.
    if (entity === 'pipeline') return ROUTES.pipelineDetail.replace(':id', id);
    if (entity === 'sales_order' || entity === 'sales_orders') return ROUTES.dispatchOrderDetail.replace(':id', id);
    if (entity === 'direct_sale') return ROUTES.invoiceDetail.replace(':id', id);
    if (entity === 'invoice_request') return `${ROUTES.invoices}?request=${id}`;
    if (['invoice', 'invoices', 'salesinvoice', 'tax_invoice'].includes(entity)) return `${ROUTES.invoices}?invoice=${id}`;
    if (entity === 'delivery_challan') return ROUTES.dispatchChallanDocuments.replace(':id', id);
    if (entity === 'sample') return `${ROUTES.sampling}?sample=${id}`;
    if (entity === 'task' || entity === 'tasks') return `${ROUTES.tasks}?taskId=${id}`;
  }

  // Direct entity-type mapping
  if (entity) {
    const route = ENTITY_ROUTE_MAP[entity];
    if (route) return route;
  }

  // Fallback: infer section from the notification type prefix
  const typePrefix = n.type?.split('_')[0]?.toLowerCase();
  if (typePrefix) {
    const route = TYPE_PREFIX_ROUTE_MAP[typePrefix];
    if (route) return route;
  }

  return null;
}

/** Maps related_entity_type values to app routes. */
const ENTITY_ROUTE_MAP: Record<string, string> = {
  quotation: ROUTES.quotations,
  enquiry: ROUTES.leads,
  proforma: ROUTES.proforma,
  visit: ROUTES.visits,
  service_ticket: ROUTES.serviceDesk,
  lead: ROUTES.leads,
  leads: ROUTES.leads,
  deal: ROUTES.deals,
  deals: ROUTES.deals,
  task: ROUTES.tasks,
  tasks: ROUTES.tasks,
  project: ROUTES.projects,
  projects: ROUTES.projects,
  meeting: ROUTES.meetings,
  meetings: ROUTES.meetings,
  contact: ROUTES.contacts,
  contacts: ROUTES.contacts,
  announcement: ROUTES.announcements,
  announcements: ROUTES.announcements,
  leave: ROUTES.leaves,
  leaves: ROUTES.leaves,
  attendance: ROUTES.attendance,
  demo: ROUTES.demos,
  demos: ROUTES.demos,
  invoice: ROUTES.accountsSales,
  invoices: ROUTES.accountsSales,
  sales_order: ROUTES.dispatchOrders,
  sales_orders: ROUTES.dispatchOrders,
  bill: ROUTES.accountsPurchases,
  bills: ROUTES.accountsPurchases,
  expense: ROUTES.accountsExpenses,
  expenses: ROUTES.accountsExpenses,
  payment: ROUTES.accountsTransactions,
  payments: ROUTES.accountsTransactions,
  mail_message: ROUTES.mailInbox,
  pipeline: ROUTES.deals,
  sample: ROUTES.sampling,
  invoice_request: ROUTES.invoices,
  delivery_challan: ROUTES.dispatchChallans,
  time_log: ROUTES.attendance,
  job: ROUTES.scraper,
};

/** Maps notification type prefixes to routes as a last resort. */
const TYPE_PREFIX_ROUTE_MAP: Record<string, string> = {
  task: ROUTES.tasks,
  deal: ROUTES.deals,
  lead: ROUTES.leads,
  project: ROUTES.projects,
  meeting: ROUTES.meetings,
  demo: ROUTES.demos,
  leave: ROUTES.leaves,
  attendance: ROUTES.attendance,
  announcement: ROUTES.announcements,
  contact: ROUTES.contacts,
  invoice: ROUTES.accountsSales,
  bill: ROUTES.accountsPurchases,
  expense: ROUTES.accountsExpenses,
  payment: ROUTES.accountsTransactions,
  mail: ROUTES.mailInbox,
};
